import { Utils } from 'src/app/common/utils';
import type {
    DropdownData,
    RepeatableSubFieldDataLoader,
    RepeatableSubFieldDependencyContext,
} from '@fitogest/reactive-form-builder/interfaces';
import type { ProductosModel } from 'src/app/models/productos/productos.model';
import type { Uso } from 'src/app/models/productos/uso.model';
import type { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';

export type TratamientosCachedProductDetail = {
    usos: Array<{ label: string; value: Uso | null }>;
    agentes: Array<{ label: string; value: Uso | null }>;
};

type ProductDropdownOption = { label: string; value: ProductosModel };
type AgenteDropdownOption = { label: string; value: Uso };
type TratamientosRepeatableDropdownKey = 'uso' | 'agente';

type RowDropdownState = {
    usos: DropdownData<Uso>;
    agentes: DropdownData<Uso>;
    productId: string;
    registro: string;
};

const createDropdownData = <T>(): DropdownData<T> => ({ filtered: [], values: [] });

/**
 * Coordinates repeatable producto -> uso -> agente dependencies for Tratamientos.
 *
 * The component still owns HTTP services, but row-aware option state and MAGRAMA
 * dependency resolution live here so the repeatable can evolve without global dropdown triggers.
 */
export class TratamientosDropdownDataSourceFactory {
    private readonly productById = new Map<string, ProductosModel>();
    private readonly productDetailCache = new Map<string, TratamientosCachedProductDetail>();
    private readonly productDetailRequests = new Map<string, Promise<TratamientosCachedProductDetail>>();
    private readonly rowStates = new Map<number, RowDropdownState>();
    private readonly lastSyncedDosisCuaderno = new Map<number, string>();

    private allProducts: ProductosModel[] = [];
    private selectableProducts: ProductosModel[] = [];
    private allProductOptions: ProductDropdownOption[] = [];
    private selectableProductOptions: ProductDropdownOption[] = [];
    private allAgenteOptions: AgenteDropdownOption[] = [];
    private shouldUseDistinctAgentes = false;

    public constructor(
        private readonly loadProductDetail: (product: ProductosModel) => Promise<TratamientosCachedProductDetail>,
    ) {}

    public setProducts(products: ProductosModel[], includeAllProducts: boolean): void {
        this.productById.clear();

        const allProducts = new Map<string, ProductosModel>();
        const selectableProducts = new Map<string, ProductosModel>();

        for (const item of products) {
            const productId = String(item.id ?? '').trim();

            if (productId === '' || allProducts.has(productId)) {
                continue;
            }

            const product = this.normalizeProductForDropdown(item);

            this.productById.set(productId, product);
            allProducts.set(productId, product);

            if (includeAllProducts || this.isTreatmentProduct(product)) {
                selectableProducts.set(productId, product);
            }
        }

        const sortByName = (first: ProductosModel, second: ProductosModel): number => {
            return String(first.nombre ?? '').localeCompare(String(second.nombre ?? ''));
        };

        this.allProducts = [...allProducts.values()].sort(sortByName);
        this.selectableProducts = [...selectableProducts.values()].sort(sortByName);
        this.allProductOptions = this.mapProductsToOptions(this.allProducts);
        this.selectableProductOptions = this.mapProductsToOptions(this.selectableProducts);
    }

    public setDistinctAgentes(agentes: Uso[], isEnabled: boolean): void {
        this.shouldUseDistinctAgentes = isEnabled;
        this.allAgenteOptions = this.deduplicateOptionsByLabel(
            agentes
                .map((item) => ({ label: String(item.agente ?? '').trim(), value: item }))
                .filter((item) => item.label !== '')
        );
    }

    public getAllProducts(): ProductosModel[] {
        return [...this.allProducts];
    }

    public getAllProductOptions(): ProductDropdownOption[] {
        return [...this.allProductOptions];
    }

    public getSelectableProductOptions(selectedProductIds: Set<string>): ProductDropdownOption[] {
        const optionByProductId = new Map<string, ProductDropdownOption>();

        for (const option of this.selectableProductOptions) {
            optionByProductId.set(String(option.value.id ?? ''), option);
        }

        for (const productId of selectedProductIds) {
            if (optionByProductId.has(productId)) {
                continue;
            }

            const selectedProduct = this.productById.get(productId);

            if (selectedProduct) {
                optionByProductId.set(productId, {
                    label: this.buildProductDropdownLabel(selectedProduct),
                    value: selectedProduct,
                });
            }
        }

        return [...optionByProductId.values()]
            .sort((first, second) => String(first.value.nombre ?? '').localeCompare(String(second.value.nombre ?? '')));
    }

    public getProductById(productId: string): ProductosModel | null {
        return this.productById.get(productId) ?? null;
    }


    public async primeRowsFromModel(model: TratamientosModel): Promise<void> {
        const productIds = this.splitLegacyAggregate(model.ids_productos);
        const usageIds = this.splitLegacyAggregate(model.ids_usos);
        const usageNames = this.splitLegacyAggregate(model.nombres_usos);
        const agentIds = this.splitLegacyAggregate(model.ids_agentes);
        const agentNames = this.splitLegacyAggregate(model.nombres_agentes);
        const maxRows = Math.max(productIds.length, usageIds.length, usageNames.length, agentIds.length, agentNames.length);

        for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            const product = this.getProductById(productIds[rowIndex] ?? '');

            if (!product) {
                this.clearRow(rowIndex);
                continue;
            }

            await this.ensureProductDetailReady(product);
            const usos = this.getUsosForProduct(product);
            const usage = this.resolveUso(usos, usageIds[rowIndex] ?? '', usageNames[rowIndex] ?? '');
            const agentes = this.getAgentesForProduct(product, usage);
            const state = this.getRowState(rowIndex);

            state.productId = String(product.id ?? '').trim();
            state.registro = String(product.num_registro ?? '').trim();
            state.usos = usos;
            state.agentes = this.ensureSelectedAgentOption(agentes, agentIds[rowIndex] ?? '', agentNames[rowIndex] ?? '');
        }
    }

    public getCachedProductDetailCount(): number {
        return this.productDetailCache.size;
    }

    public getLoadData(key: TratamientosRepeatableDropdownKey): RepeatableSubFieldDataLoader {
        return (ctx: RepeatableSubFieldDependencyContext): Promise<DropdownData<unknown>> => this.loadData(ctx, key);
    }

    public getData(key: TratamientosRepeatableDropdownKey, rowIndex: number): DropdownData<unknown> {
        const state = this.getRowState(rowIndex);

        if (key === 'uso') {
            return state.usos as DropdownData<unknown>;
        }

        return state.agentes as DropdownData<unknown>;
    }

    public patchUnitsAfterAgentChange(ctx: RepeatableSubFieldDependencyContext): Record<string, unknown> {
        const product = this.resolveProductFromContext(ctx);
        const agent = this.resolveAgentFromContext(ctx, product);
        const unitSource = product?.unidades
            ?? product?.uds_medida
            ?? agent?.unidad_medida
            ?? agent?.unidades_de_medida
            ?? agent?.unidad
            ?? '';
        const cuadernoUnit = this.normalizeBaseUnit(unitSource);
        const appliedUnit = this.normalizeDoseUnit(unitSource);
        const currentCuadernoUnit = String(ctx.row['unidadMedidaCuaderno'] ?? '').trim();
        const currentAppliedUnit = String(ctx.row['unidadMedida'] ?? '').trim();
        const patch: Record<string, unknown> = {};

        if (cuadernoUnit !== '' && currentCuadernoUnit !== cuadernoUnit) {
            patch['unidadMedidaCuaderno'] = cuadernoUnit;
        }

        if (appliedUnit !== '' && currentAppliedUnit !== appliedUnit) {
            patch['unidadMedida'] = appliedUnit;
        }

        return patch;
    }

    public patchAppliedDoseAfterNotebookDoseChange(ctx: RepeatableSubFieldDependencyContext): Record<string, unknown> {
        const dosisCuaderno = String(ctx.changedValue ?? '').trim();
        const dosisReal = String(ctx.row['dosisReal'] ?? '').trim();
        const previousCuaderno = this.lastSyncedDosisCuaderno.get(ctx.rowIndex);

        this.lastSyncedDosisCuaderno.set(ctx.rowIndex, dosisCuaderno);

        if (dosisCuaderno !== '' && (dosisReal === '' || dosisReal === previousCuaderno)) {
            return { dosisReal: dosisCuaderno };
        }

        return {};
    }

    public getUsosForProduct(product: ProductosModel | null): DropdownData<Uso> {
        if (!product || !product.num_registro || this.isFertilizerProduct(product)) {
            return this.emptyDropdown('Uso...');
        }

        const detail = this.productDetailCache.get(String(product.num_registro));

        if (!detail) {
            return this.emptyDropdown('Cargando...');
        }

        return {
            values: detail.usos,
            filtered: detail.usos,
        };
    }

    public getAgentesForProduct(product: ProductosModel | null, uso: Uso | null): DropdownData<Uso> {
        if (!product) {
            return this.emptyDropdown('Agente...');
        }

        if (this.isFertilizerProduct(product)) {
            return this.getFertilizerAgentesDropdown();
        }

        if (!product.num_registro) {
            return this.emptyDropdown('Agente...');
        }

        const detail = this.productDetailCache.get(String(product.num_registro));

        if (!detail) {
            return this.emptyDropdown('Cargando...');
        }

        const filteredAgentes = uso
            ? detail.agentes.filter((item) => {
                return this.normalizeOptionLabel(item.value?.uso ?? '') === this.normalizeOptionLabel(uso.uso ?? '');
            })
            : detail.agentes;
        const resolvedAgentes = filteredAgentes.length > 0 || !uso ? filteredAgentes : detail.agentes;
        const deduplicatedAgentes = this.deduplicateOptionsByLabel(resolvedAgentes);
        const values = [{ label: 'Agente...', value: null }, ...deduplicatedAgentes];

        return {
            values,
            filtered: values,
        };
    }

    private async loadData(
        ctx: RepeatableSubFieldDependencyContext,
        key: TratamientosRepeatableDropdownKey,
    ): Promise<DropdownData<unknown>> {
        const rowIndex = this.normalizeRowIndex(ctx.rowIndex);
        const product = this.resolveProductFromContext(ctx);

        if (!product) {
            this.clearRow(rowIndex);

            return this.getData(key, rowIndex);
        }

        await this.ensureProductDetailReady(product);
        const uso = key === 'agente' ? this.resolveUsoFromContext(ctx, product) : null;
        const state = this.getRowState(rowIndex);

        state.productId = String(product.id ?? '').trim();
        state.registro = String(product.num_registro ?? '').trim();
        state.usos = this.getUsosForProduct(product);
        state.agentes = this.getAgentesForProduct(product, uso);

        return this.getData(key, rowIndex);
    }

    private async ensureProductDetailReady(product: ProductosModel): Promise<void> {
        if (this.isFertilizerProduct(product)) {
            return;
        }

        const registro = String(product.num_registro ?? '').trim();

        if (registro === '' || this.productDetailCache.has(registro)) {
            return;
        }

        const pendingRequest = this.productDetailRequests.get(registro);

        if (pendingRequest) {
            await pendingRequest;

            return;
        }

        const request = this.loadProductDetail(product)
            .then((detail) => {
                this.productDetailCache.set(registro, detail);

                return detail;
            })
            .finally(() => {
                this.productDetailRequests.delete(registro);
            });

        this.productDetailRequests.set(registro, request);
        await request;
    }

    private resolveProductFromContext(ctx: RepeatableSubFieldDependencyContext): ProductosModel | null {
        const resolvedEntity = ctx.resolveEntity?.<ProductosModel>('producto') ?? null;

        if (resolvedEntity) {
            return resolvedEntity;
        }

        const raw = ctx.row['producto'] ?? ctx.changedValue;

        if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
            return raw as ProductosModel;
        }

        const productId = String(raw ?? '').trim();

        if (productId === '') {
            return null;
        }

        return this.getProductById(productId);
    }

    private resolveUsoFromContext(ctx: RepeatableSubFieldDependencyContext, product: ProductosModel): Uso | null {
        const resolvedEntity = ctx.resolveEntity?.<Uso>('uso') ?? null;

        if (resolvedEntity) {
            return resolvedEntity;
        }

        const raw = ctx.row['uso'];

        if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
            return raw as Uso;
        }

        const usageName = String(ctx.row['usoNombre'] ?? '').trim();
        const resolvedByName = this.findUsoByName(this.getUsosForProduct(product), usageName);

        if (resolvedByName) {
            return resolvedByName;
        }

        const usageId = String(raw ?? '').trim();

        if (usageId === '') {
            return null;
        }

        return this.getUsosForProduct(product).values
            .map((option) => option.value)
            .filter((item): item is Uso => item !== null)
            .find((item) => String(item.id ?? item.id_uso ?? '') === usageId) ?? null;
    }


    private resolveAgentFromContext(ctx: RepeatableSubFieldDependencyContext, product: ProductosModel | null): Uso | null {
        const resolvedEntity = ctx.resolveEntity?.<Uso>('agente') ?? null;

        if (resolvedEntity) {
            return resolvedEntity;
        }

        const raw = ctx.changedKey === 'agente' ? ctx.changedValue : ctx.row['agente'];

        if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
            return raw as Uso;
        }

        const usage = product ? this.resolveUsoFromContext(ctx, product) : null;
        const options = this.getAgentesForProduct(product, usage);
        const agentName = String(ctx.row['agenteNombre'] ?? '').trim();
        const resolvedByName = this.findAgenteByName(options, agentName);

        if (resolvedByName) {
            return resolvedByName;
        }

        const agentId = String(raw ?? '').trim();

        if (agentId === '') {
            return null;
        }

        return options.values
            .map((option) => option.value)
            .filter((item): item is Uso => item !== null)
            .find((item) => String(item.id ?? item.id_uso ?? '') === agentId) ?? null;
    }

    private resolveUso(options: DropdownData<Uso>, usageId: string, usageName: string): Uso | null {
        const normalizedId = String(usageId ?? '').trim();
        const normalizedName = this.normalizeOptionLabel(usageName);

        return options.values
            .map((option) => option.value)
            .filter((item): item is Uso => item !== null)
            .find((item) => {
                const itemId = String(item.id ?? item.id_uso ?? '').trim();
                const itemName = this.normalizeOptionLabel(item.uso ?? '');

                return (normalizedId !== '' && itemId === normalizedId)
                    || (normalizedName !== '' && itemName === normalizedName);
            }) ?? null;
    }

    private ensureSelectedAgentOption(options: DropdownData<Uso>, agentId: string, agentName: string): DropdownData<Uso> {
        const normalizedId = String(agentId ?? '').trim();
        const normalizedName = this.normalizeOptionLabel(agentName);

        if (normalizedId === '' && normalizedName === '') {
            return options;
        }

        const existingOption = options.values.find((option) => {
            const item = option.value;

            if (!item) {
                return false;
            }

            const itemId = String(item.id ?? item.id_uso ?? '').trim();
            const itemName = this.normalizeOptionLabel(item.agente ?? '');

            return (normalizedId !== '' && itemId === normalizedId)
                || (normalizedName !== '' && itemName === normalizedName);
        });

        if (existingOption) {
            return options;
        }

        if (normalizedId === '' || normalizedName === '') {
            return options;
        }

        const syntheticAgent: Uso = {
            id: normalizedId,
            id_uso: normalizedId,
            uso: '',
            agente: agentName,
            minDosis: 0,
            maxDosis: 0,
            dosis_min: 0,
            dosis_max: 0,
            isInvalid: false,
        } as Uso;
        const syntheticOption = { label: agentName, value: syntheticAgent };
        const values = [...options.values, syntheticOption];

        return {
            values,
            filtered: [...options.filtered, syntheticOption],
        };
    }

    private splitLegacyAggregate(value: unknown): string[] {
        return String(value ?? '')
            .split(';')
            .map((item) => item.trim());
    }

    private getRowState(rowIndex: number): RowDropdownState {
        const normalizedRowIndex = this.normalizeRowIndex(rowIndex);
        const existingState = this.rowStates.get(normalizedRowIndex);

        if (existingState) {
            return existingState;
        }

        const state: RowDropdownState = {
            usos: createDropdownData<Uso>(),
            agentes: createDropdownData<Uso>(),
            productId: '',
            registro: '',
        };

        this.rowStates.set(normalizedRowIndex, state);

        return state;
    }

    private clearRow(rowIndex: number): void {
        const state = this.getRowState(rowIndex);

        state.usos = createDropdownData<Uso>();
        state.agentes = createDropdownData<Uso>();
        state.productId = '';
        state.registro = '';
    }

    private normalizeRowIndex(rowIndex: number | undefined): number {
        return Number.isFinite(rowIndex) && rowIndex !== undefined ? rowIndex : 0;
    }

    private emptyDropdown(label: string): DropdownData<Uso> {
        return {
            values: [{ label, value: null }],
            filtered: [{ label, value: null }],
        };
    }

    private getFertilizerAgentesDropdown(): DropdownData<Uso> {
        if (!this.shouldUseDistinctAgentes || this.allAgenteOptions.length === 0) {
            return this.emptyDropdown('Agente...');
        }

        const values = [{ label: 'Agente...', value: null }, ...this.allAgenteOptions];

        return {
            values,
            filtered: values,
        };
    }

    private mapProductsToOptions(products: ProductosModel[]): ProductDropdownOption[] {
        return products.map((item) => ({
            label: this.buildProductDropdownLabel(item),
            value: item,
        }));
    }

    private buildProductDropdownLabel(product: ProductosModel): string {
        const prefix = this.getProductDropdownPrefix(product);
        const productName = String(product.nombre ?? '').trim();

        return `${prefix} ${productName}`.trim();
    }

    private getProductDropdownPrefix(product: ProductosModel): string {
        const productType = String(product.tipo ?? '').trim().toLowerCase();

        if (productType === 'fertilizante') {
            return Utils.checkIfProductIsValid(product.fecha_caducidad, productType);
        }

        const registrationNumber = String(product.num_registro ?? '').trim();

        if (productType === 'fitosanitario' && registrationNumber === '') {
            return Utils.checkIfProductIsValid('', productType);
        }

        if (String(product.fecha_caducidad ?? '').trim() !== '') {
            return Utils.checkIfProductIsValid(product.fecha_caducidad, productType);
        }

        return '';
    }

    private normalizeProductForDropdown(product: ProductosModel): ProductosModel {
        return {
            ...product,
            nombre: String(product.nombre ?? '').trim().toUpperCase(),
        };
    }

    private isTreatmentProduct(product: ProductosModel): boolean {
        return product.tipo === 'fitosanitario' || product.tipo === 'fertilizante';
    }

    private isFertilizerProduct(product: ProductosModel): boolean {
        return String(product.tipo ?? '').trim().toLowerCase() === 'fertilizante';
    }

    private normalizeOptionLabel(value: unknown): string {
        return String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    private deduplicateOptionsByLabel<T extends { label: string }>(options: T[]): T[] {
        const seenLabels = new Set<string>();

        return options.filter((item) => {
            const normalizedLabel = this.normalizeOptionLabel(item.label);

            if (normalizedLabel === '' || seenLabels.has(normalizedLabel)) {
                return false;
            }

            seenLabels.add(normalizedLabel);
            return true;
        });
    }

    private findUsoByName(options: DropdownData<Uso>, usoLabel: string): Uso | null {
        const normalizedLabel = this.normalizeOptionLabel(usoLabel);

        if (normalizedLabel === '') {
            return null;
        }

        return options.values
            .map((item) => item.value)
            .filter((item): item is Uso => item !== null)
            .find((item) => this.normalizeOptionLabel(item.uso) === normalizedLabel) ?? null;
    }

    private findAgenteByName(options: DropdownData<Uso>, agenteLabel: string): Uso | null {
        const normalizedLabel = this.normalizeOptionLabel(agenteLabel);

        if (normalizedLabel === '') {
            return null;
        }

        return options.values
            .map((item) => item.value)
            .filter((item): item is Uso => item !== null)
            .find((item) => this.normalizeOptionLabel(item.agente) === normalizedLabel) ?? null;
    }

    private normalizeBaseUnit(rawUnit: string | number | boolean | null | undefined): string {
        const unit = String(rawUnit ?? '').trim().toLowerCase();

        if (['kg', 'kilo', 'kilos', 'kilogramo', 'kilogramos'].includes(unit)) {
            return 'kg';
        }

        if (['l', 'litro', 'litros'].includes(unit)) {
            return 'L';
        }

        if (unit === 'kg/ha') {
            return 'kg';
        }

        if (unit === 'l/ha') {
            return 'L';
        }

        return '';
    }

    private normalizeDoseUnit(rawUnit: string | number | boolean | null | undefined): string {
        const unit = String(rawUnit ?? '').trim().toLowerCase();

        if (['kg', 'kilo', 'kilos', 'kilogramo', 'kilogramos', 'kg/ha'].includes(unit)) {
            return 'kg/ha';
        }

        if (['l', 'litro', 'litros', 'l/ha'].includes(unit)) {
            return 'L/ha';
        }

        return '';
    }
}
